import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { ChevronDown, Search, Sparkles, X } from 'lucide-react';
import {
  EMOJI_LIST,
  GLYPH_LIST,
  TONES,
  TONE_STYLES,
  emojiLabel,
  emojiTileBackground,
  glyphLabel,
  useTravelIcons,
} from './travelIcons';
import styles from './TravelIconPicker.module.css';

/** One emoji on its little scene, the way a link row shows it on the website. */
export function EmojiTile({ name, icons, size = 36 }) {
  const data = icons?.emoji?.[name];
  return (
    <span
      className={styles.emojiTile}
      style={{ width: size, height: size, background: emojiTileBackground(name) }}
    >
      {data ? <Icon icon={data} width={Math.round(size * 0.84)} height={Math.round(size * 0.84)} /> : null}
    </span>
  );
}

/** A card-header glyph on its tinted tile. */
export function GlyphTile({ name, tone, icons, size = 52 }) {
  const data = icons?.glyphs?.[name];
  const toneStyle = TONE_STYLES[tone] || TONE_STYLES.blue;
  return (
    <span
      className={styles.glyphTile}
      style={{ width: size, height: size, background: toneStyle.background, color: toneStyle.color }}
    >
      {data ? <Icon icon={data} width={Math.round(size * 0.6)} height={Math.round(size * 0.6)} /> : null}
    </span>
  );
}

/**
 * Pick an icon from the travel set, or leave it on Automatic.
 *
 *   kind      'emoji' for a link row, 'glyph' for a card header
 *   value     the chosen icon's name, '' for Automatic
 *   autoName  what Automatic currently resolves to, so the trigger shows the real result
 *   tone      the card's tone, which tints the glyphs
 *   onChange  receives the new name, or '' for Automatic
 *
 * Where Automatic is not a catalogue icon (the website header keeps its own built-in icons),
 * pass what it really shows instead of autoName:
 *   autoTile  (size) => node drawn for Automatic
 *   autoLabel its name, e.g. "Built-in icon"
 *   autoHint  the line under "Automatic" in the list
 */
export default function TravelIconPicker({
  kind, value, autoName, tone, onChange, label, autoTile, autoLabel, autoHint,
}) {
  const icons = useTravelIcons();
  const [open, setOpen] = useState(false);
  const [placeUp, setPlaceUp] = useState(false);
  const [query, setQuery] = useState('');
  const boxRef = useRef(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);

  const isAuto = !value;
  const shown = value || autoName;
  const nameOf = kind === 'emoji' ? emojiLabel : glyphLabel;

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open && kind === 'emoji') searchRef.current?.focus();
    if (!open) setQuery('');
  }, [open, kind]);

  const options = useMemo(() => {
    if (kind !== 'emoji') return GLYPH_LIST;
    const q = query.trim().toLowerCase();
    if (!q) return EMOJI_LIST;
    return EMOJI_LIST.filter(
      (e) =>
        e.label.toLowerCase().includes(q) ||
        e.name.includes(q) ||
        e.keywords.some((k) => k.toLowerCase().includes(q))
    );
  }, [kind, query]);

  const pick = (name) => {
    onChange(name);
    setOpen(false);
    triggerRef.current?.focus();
  };

  // Near the foot of the screen the list would run under the sticky Save bar, so it opens
  // upwards there instead (the panel is at most 360px, plus the bar's height).
  const toggle = () => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (r && !open) {
      const below = window.innerHeight - r.bottom;
      setPlaceUp(below < 440 && r.top > below);
    }
    setOpen((o) => !o);
  };

  const renderTile = (name, size) =>
    kind === 'emoji'
      ? <EmojiTile name={name} icons={icons} size={size} />
      : <GlyphTile name={name} tone={tone} icons={icons} size={size} />;
  const renderAuto = (size) => (autoTile ? autoTile(size) : renderTile(autoName, size));

  const triggerTitle = isAuto
    ? `${label}: Automatic (${autoLabel || nameOf(shown)})`
    : `${label}: ${nameOf(shown)}`;

  return (
    <div className={styles.picker} ref={boxRef}>
      <button
        ref={triggerRef}
        type="button"
        className={`${styles.trigger} ${kind === 'glyph' ? styles.triggerGlyph : ''} ${isAuto ? styles.triggerAuto : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={triggerTitle}
        title={triggerTitle}
        onClick={toggle}
      >
        {isAuto ? renderAuto(kind === 'emoji' ? 34 : 50) : renderTile(shown, kind === 'emoji' ? 34 : 50)}
        <span className={styles.caret} aria-hidden="true"><ChevronDown size={10} strokeWidth={3} /></span>
        {isAuto && <span className={styles.autoBadge} aria-hidden="true">Auto</span>}
      </button>

      {open && (
        <div
          className={`${styles.popover} ${placeUp ? styles.popoverUp : ''}`}
          role="dialog"
          aria-label={`Choose ${label.toLowerCase()}`}
        >
          {kind === 'emoji' && (
            <div className={styles.search}>
              <Search size={14} aria-hidden="true" />
              <input
                ref={searchRef}
                value={query}
                placeholder="Search icons, e.g. beach, Paris, ski"
                aria-label="Search icons"
                onChange={(e) => setQuery(e.target.value)}
              />
              {query && (
                <button type="button" className={styles.searchClear} aria-label="Clear search" onClick={() => setQuery('')}>
                  <X size={13} />
                </button>
              )}
            </div>
          )}

          <button
            type="button"
            className={`${styles.autoOption} ${isAuto ? styles.optionOn : ''}`}
            aria-pressed={isAuto}
            onClick={() => pick('')}
          >
            {renderAuto(30)}
            <span className={styles.autoText}>
              <strong><Sparkles size={12} aria-hidden="true" /> Automatic</strong>
              <span>
                {autoHint ?? `${kind === 'emoji' ? 'From the link text and place' : 'From the card title'}: ${nameOf(autoName)}`}
              </span>
            </span>
          </button>

          {options.length > 0 ? (
            <div className={`${styles.grid} ${kind === 'glyph' ? styles.gridGlyph : ''}`}>
              {options.map((o) => (
                <button
                  key={o.name}
                  type="button"
                  className={`${styles.option} ${value === o.name ? styles.optionOn : ''}`}
                  aria-pressed={value === o.name}
                  aria-label={o.label}
                  title={o.label}
                  onClick={() => pick(o.name)}
                >
                  {renderTile(o.name, kind === 'emoji' ? 32 : 38)}
                </button>
              ))}
            </div>
          ) : (
            <p className={styles.empty}>No icon matches “{query.trim()}”.</p>
          )}
        </div>
      )}
    </div>
  );
}

/** The card's colour: Automatic, or one of the website's seven tones. */
export function ToneSwatches({ value, autoTone, onChange }) {
  const isAuto = !TONES.includes(value);
  return (
    <div className={styles.tones} role="group" aria-label="Card colour">
      <button
        type="button"
        className={`${styles.toneAuto} ${isAuto ? styles.toneOn : ''}`}
        aria-pressed={isAuto}
        title={`Automatic (${autoTone})`}
        onClick={() => onChange('')}
      >
        <span className={styles.toneDot} style={{ background: TONE_STYLES[autoTone]?.color }} aria-hidden="true" />
        Auto
      </button>
      {TONES.map((t) => (
        <button
          key={t}
          type="button"
          className={`${styles.tone} ${value === t ? styles.toneOn : ''}`}
          style={{ background: TONE_STYLES[t].background }}
          aria-pressed={value === t}
          aria-label={t}
          title={t[0].toUpperCase() + t.slice(1)}
          onClick={() => onChange(t)}
        >
          <span className={styles.toneDot} style={{ background: TONE_STYLES[t].color }} aria-hidden="true" />
        </button>
      ))}
    </div>
  );
}
